import { useState, useEffect } from 'react'
import { dataService } from '../services/dataService'
import { Plus, Trash2, TrendingUp, WifiOff, EyeOff, Eye } from 'lucide-react'
import ProductoDialog from './ProductoDialog'

export default function Productos({ usuario }) {
  const [productos, setProductos] = useState([])
  const [loading, setLoading] = useState(true)
  const [productoEditar, setProductoEditar] = useState(null)
  const [mostrarDialog, setMostrarDialog] = useState(false)
  const [seleccionados, setSeleccionados] = useState([])
  const [ajustePrecio, setAjustePrecio] = useState('')
  const [dataSource, setDataSource] = useState('online')
  const [mostrarInactivos, setMostrarInactivos] = useState(false)

  useEffect(() => {
    cargarProductos()
  }, [])

  async function cargarProductos() {
    try {
      const productosRes = await dataService.getProducts(false) // traer todos, filtramos en el componente
      setDataSource(productosRes.source)
      const productosOrdenados = (productosRes.data || []).sort((a, b) => {
        if (a.activo !== b.activo) return b.activo - a.activo
        return a.nombre.localeCompare(b.nombre)
      })
      setProductos(productosOrdenados)
    } catch (error) {
      console.error('Error cargando productos:', error)
      alert('Error al cargar productos')
    } finally {
      setLoading(false)
    }
  }

  function abrirNuevoProducto() {
    setProductoEditar(null)
    setMostrarDialog(true)
  }

  function abrirEditarProducto(producto) {
    setProductoEditar(producto)
    setMostrarDialog(true)
  }

  async function eliminarProducto(id) {
    if (!confirm('¿Marcar este producto como inactivo?\n\nPodrás volver a verlo activando "Mostrar inactivos".')) return
    try {
      const result = await dataService.deleteProduct(id)
      if (result.success) {
        const mensaje = result.mode === 'offline'
          ? 'Producto marcado como inactivo OFFLINE. Se sincronizará cuando haya conexión.'
          : 'Producto marcado como inactivo correctamente.'
        alert(mensaje)
        cargarProductos()
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Error: ' + error.message)
    }
  }

  function toggleSeleccion(id) {
    setSeleccionados(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  function seleccionarTodos() {
    const activos = productosFiltrados.map(p => p.id)
    if (seleccionados.length === activos.length) {
      setSeleccionados([])
    } else {
      setSeleccionados(activos)
    }
  }

  async function aplicarAjusteMasivo() {
    if (seleccionados.length === 0) {
      alert('Selecciona al menos un producto')
      return
    }
    const ajuste = parseFloat(ajustePrecio)
    if (isNaN(ajuste)) {
      alert('Introduce un valor numérico válido')
      return
    }
    if (!confirm(`¿Aplicar ${ajuste >= 0 ? '+' : ''}${ajuste.toFixed(2)}€ a ${seleccionados.length} producto(s)?`)) return

    try {
      for (const id of seleccionados) {
        const producto = productos.find(p => p.id === id)
        const nuevoPrecio = Number(producto.precio_base) + ajuste
        if (nuevoPrecio < 0) {
          alert(`El producto "${producto.nombre}" tendría precio negativo. Operación cancelada.`)
          return
        }
        await dataService.saveProduct({ ...producto, precio_base: nuevoPrecio }, false)
      }
      const mensaje = navigator.onLine
        ? 'Precios actualizados correctamente'
        : 'Precios actualizados OFFLINE. Se sincronizarán cuando haya conexión.'
      alert(mensaje)
      setSeleccionados([])
      setAjustePrecio('')
      cargarProductos()
    } catch (error) {
      console.error('Error:', error)
      alert('Error al actualizar precios: ' + error.message)
    }
  }

  const esAdmin = usuario?.rol === 'admin'
  const productosFiltrados = mostrarInactivos ? productos : productos.filter(p => p.activo)
  const numInactivos = productos.filter(p => !p.activo).length

  return (
    <div>
      {dataSource === 'cache' && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid var(--warn)',
          borderRadius: '6px',
          fontSize: '0.875rem',
          color: 'var(--warn)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <WifiOff size={18} />
          <span>📵 Modo offline - Puedes crear/editar/eliminar. Se sincronizará al conectar.</span>
        </div>
      )}

      <div className="seccion-header">
        <h2>Gestión de Productos</h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {numInactivos > 0 && (
            <button
              onClick={() => setMostrarInactivos(v => !v)}
              className="btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }}
            >
              {mostrarInactivos ? <Eye size={16} /> : <EyeOff size={16} />}
              {mostrarInactivos ? 'Ocultar inactivos' : `Inactivos (${numInactivos})`}
            </button>
          )}
          <button onClick={abrirNuevoProducto} className="btn-primary">
            <Plus size={20} /> Nuevo producto
          </button>
        </div>
      </div>

      {seleccionados.length > 0 && (
        <div className="precio-masivo">
          <div className="precio-masivo-grupo">
            <label>{seleccionados.length} producto(s) seleccionado(s)</label>
            <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
              Introduce un valor positivo para subir o negativo para bajar
            </div>
          </div>
          <div className="precio-masivo-grupo" style={{ maxWidth: '200px' }}>
            <label>Ajuste de precio (€)</label>
            <input
              type="number"
              step="0.01"
              placeholder="Ej: 0.05 o -0.10"
              value={ajustePrecio}
              onChange={(e) => setAjustePrecio(e.target.value)}
            />
          </div>
          <button onClick={aplicarAjusteMasivo} className="btn-primary">
            <TrendingUp size={18} /> Aplicar ajuste
          </button>
        </div>
      )}

      <div className="tabla-container">
        <div className="tabla-scroll">
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>
              Cargando productos...
            </div>
          ) : productosFiltrados.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>
              No hay productos registrados
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={seleccionados.length === productosFiltrados.length && productosFiltrados.length > 0}
                      onChange={seleccionarTodos}
                    />
                  </th>
                  <th>Nombre</th>
                  <th>Precio Base</th>
                  <th>IVA</th>
                  <th>Aplica Margen</th>
                  <th>Estado</th>
                  <th style={{ width: '60px', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {productosFiltrados.map(producto => (
                  <tr
                    key={producto.id}
                    onClick={() => abrirEditarProducto(producto)}
                    style={{ cursor: 'pointer', opacity: producto.activo ? 1 : 0.5 }}
                  >
                    <td className="checkbox-cell" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={seleccionados.includes(producto.id)}
                        onChange={() => toggleSeleccion(producto.id)}
                      />
                    </td>
                    <td style={{ fontWeight: 500 }}>{producto.nombre}</td>
                    <td style={{ fontFamily: 'monospace' }}>{Number(producto.precio_base).toFixed(2)}€</td>
                    <td>{producto.iva_pct}%</td>
                    <td>
                      <span className={`badge ${producto.descuento_aplicable ? 'badge-success' : 'badge-inactive'}`}>
                        {producto.descuento_aplicable ? 'Sí' : 'No'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${producto.activo ? 'badge-success' : 'badge-inactive'}`}>
                        {producto.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    {esAdmin && (
                      <td onClick={e => e.stopPropagation()}>
                        <div className="acciones-cell">
                          <button
                            onClick={() => eliminarProducto(producto.id)}
                            className="btn-icon-danger"
                            title="Marcar inactivo"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {mostrarDialog && (
        <ProductoDialog
          producto={productoEditar}
          onClose={() => { setMostrarDialog(false); setProductoEditar(null) }}
          onGuardar={() => { setMostrarDialog(false); setProductoEditar(null); cargarProductos() }}
        />
      )}
    </div>
  )
}
